import { ref, unref, computed } from 'vue'
import { get as _get } from 'lodash-es'
import { useStore } from '@/store'
import { useRouter } from 'vue-router'
import { del, patch } from '@/api'
import { modelNamePluralize } from '@/utils'

export function useDeletableRecord(record, deleteQueryParams = {}) {
  const store = useStore()
  const router = useRouter()
  const appName = computed(() => {
    const appName = record.value.app_name || ''
    return _get(appName, 'value', appName)
  })
  const modelName = computed(() => {
    let modelName = record.value.model_name || ''
    modelName = _get(modelName, 'value', modelName)
    return modelNamePluralize(modelName)
  })
  const modelType = computed(() => {
    const modelType = record.value.model_type || ''
    return _get(modelType, 'value', modelType).toLowerCase()
  })
  const recordId = computed(() => {
    const recordId = record.value.id || ''
    return _get(recordId, 'value', recordId)
  })
  const recordName = computed(() => {
    const fieldValue = record.value?.name
    const nameValue = _get(fieldValue, 'value', fieldValue)
    return _get(nameValue, 'name', nameValue)
  })

  const deleteQueryParamsStr = computed(() => {
    deleteQueryParams = unref(deleteQueryParams)
    return new URLSearchParams(deleteQueryParams).toString()
  })
  const baseDeleteUrl = computed(() => {
    let delUrl = `${appName.value}/${modelName.value}/${recordId.value}/`
    if (deleteQueryParamsStr.value) delUrl += `?${deleteQueryParamsStr.value}`
    return delUrl
  })
  const isDeleting = ref(false)
  async function deleteRecord() {
    try {
      isDeleting.value = true
      const response = await del(baseDeleteUrl.value)
      if (response.status >= 200 && response.status < 300) {
        store.commit('notify', {
          type: 'success',
          message: `Successfully deleted ${recordName.value}`,
        })
        router.push({
          name: 'list',
          params: {
            appName: appName.value,
            modelName: modelName.value,
            modelType: modelType.value,
          },
        })
      } else {
        throw new Error(`Could not delete record ${recordId.value}`)
      }
    } catch (error) {
      const errorMessage =
        error.response.data.message ||
        error.message ||
        `Could not delete record ${recordId.value}`
      store.commit('notify', { type: 'error', message: errorMessage })
    } finally {
      isDeleting.value = false
    }
  }

  const isRestoring = ref(false)
  async function restoreRecord() {
    const payload = { results: { deleted: { value: false } } }
    try {
      isRestoring.value = true
      const response = await patch(baseDeleteUrl.value, { data: payload })
      if (response.status >= 200 && response.status < 300) {
        record.value = response.data.results
        store.commit('notify', {
          type: 'success',
          message: `Successfully restored item: ${recordName.value}`,
        })
      }
    } catch (error) {
      const errorMessage =
        error.response.data.message ||
        `Could not delete record ${recordId.value}`
      store.commit('notify', { type: 'error', message: errorMessage })
    } finally {
      isRestoring.value = false
    }
  }

  return {
    isDeleting,
    deleteRecord,
    isRestoring,
    restoreRecord,
  }
}
